// src/legal/TermsOfService.jsx
// The Terms of Service text. Changing it means bumping LEGAL_VERSION (both copies — see
// legalConfig.js), which asks every user to accept again.
//
// A plain-language draft, not reviewed by a lawyer. The advisory-only and accuracy
// clauses are the substance the product needs; the rest is conventional scaffolding.

import React from 'react';
import Value from './Value';

export default function TermsOfService({ id }) {
  return (
    <>
      <section>
        <h2>1. About these terms</h2>
        <p>
          These terms govern your use of <Value v={id.product} /> (“the Service”),
          operated by <Value v={id.operator} /> (“we”, “us”). By creating an account or
          using the Service you agree to them, and to our{' '}
          <a href="#privacy">Privacy Policy</a>. If you do not agree, do not use the
          Service.
        </p>
      </section>

      <section className="lg-callout">
        <h2>2. The Service is advisory — you make the decisions</h2>
        <p>
          The Service produces research, summaries, drafts, scores and recommendations,
          many of them generated by artificial intelligence. <strong>They are one
          additional input to your own judgement. They are not the final word</strong>,
          and they are not professional, legal, financial or other expert advice.
        </p>
        <p>
          You are solely responsible for the decisions you make and the actions you take,
          including any taken on the basis of the Service’s output. <strong>We accept no
          liability for any action you take, or decide not to take, based on
          recommendations or other output from the Service.</strong> Review everything
          before you rely on it, send it, or act on it.
        </p>
      </section>

      <section className="lg-callout">
        <h2>3. We do not vouch for accuracy</h2>
        <p>
          The Service gathers information from the public internet and from third-party
          data providers, and generates text using large language models. That
          information may be incomplete, out of date or wrong, and AI-generated content
          can state things confidently that are not true.
        </p>
        <p>
          <strong>We do not verify, and do not guarantee, the accuracy, completeness or
          timeliness of information obtained from the internet, third-party sources or
          language models.</strong> Check anything that matters against an authoritative
          source.
        </p>
      </section>

      <section>
        <h2>4. Your account</h2>
        <ul>
          <li>Access may require approval by an administrator, and can be withdrawn.</li>
          <li>Keep your sign-in credentials secure. You are responsible for activity under
            your account.</li>
          <li>Give accurate information when you sign up, and keep it current.</li>
        </ul>
      </section>

      <section>
        <h2>5. Your data</h2>
        <p>
          You keep ownership of the data you put into the Service. You give us permission
          to store and process it only as needed to provide the Service to you, as
          described in the <a href="#privacy">Privacy Policy</a>. You are responsible for
          having the right to upload the data you add, including information about other
          people.
        </p>
      </section>

      <section>
        <h2>6. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>break the law, or use the Service to send spam or unlawful communications;</li>
          <li>try to access data or accounts that are not yours, or probe, disrupt or
            overload the Service;</li>
          <li>upload malicious code, or content you have no right to use;</li>
          <li>resell or provide the Service to others without our written agreement.</li>
        </ul>
      </section>

      <section>
        <h2>7. No warranty</h2>
        <p>
          The Service is provided “as is” and “as available”. To the fullest extent the
          law allows, we give no warranties of any kind, express or implied, including
          that the Service will be uninterrupted, error-free, or fit for a particular
          purpose.
        </p>
      </section>

      <section>
        <h2>8. Limitation of liability</h2>
        <p>
          To the fullest extent the law allows, we are not liable for any indirect,
          incidental, special or consequential loss, or for lost profits, revenue, data
          or business opportunity, arising from your use of the Service or reliance on
          its output. Nothing in these terms limits liability that cannot be limited by
          law.
        </p>
      </section>

      <section>
        <h2>9. Ending your use</h2>
        <p>
          You may stop using the Service at any time, and ask for your data to be deleted
          as described in the <a href="#privacy">Privacy Policy</a>. We may suspend or
          end access if these terms are broken, or if we stop offering the Service.
        </p>
      </section>

      <section>
        <h2>10. Changes to these terms</h2>
        <p>
          We may update these terms. When we do, the date at the top changes and you will
          be asked to accept the new version before you continue using the Service.
        </p>
      </section>

      <section>
        <h2>11. Governing law and contact</h2>
        <p>
          These terms are governed by the laws of <Value v={id.jurisdiction} />.
          Questions: <Value v={id.contactEmail} mail />.
        </p>
      </section>
    </>
  );
}
